import { useState, useEffect, useCallback } from 'react'
import { CHAT_API_BASE_URL_READ, getChatAccessToken } from '../config'

interface Session {
  id: string
  user_id?: number
  title?: string | null
  status?: string
  company_id?: number | null
  fy_id?: number | null
  started_at?: string
  last_activity_at?: string
}

/** Normalize API response (session_id, created_at) to Session (id, ...) */
function normalizeSession(data: Record<string, unknown>): Session {
  return {
    id: (data.session_id ?? data.id) as string,
    user_id: data.user_id as number | undefined,
    title: (data.title as string | null) ?? null,
    status: (data.status as string) ?? 'active',
    company_id: (data.company_id as number | null) ?? null,
    fy_id: (data.fy_id as number | null) ?? null,
    started_at: (data.started_at ?? data.created_at) as string,
    last_activity_at: (data.last_activity_at ?? data.updated_at) as string,
  }
}

const SESSION_STORAGE_KEY = 'chatbot_session'

/**
 * Check if a string is a valid UUID
 */
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

export function useChatSession() {
  const getAccessToken = getChatAccessToken
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // No session persistence for now: always start fresh on refresh.
  useEffect(() => {
    localStorage.removeItem(SESSION_STORAGE_KEY)
  }, [])

  const createSession = useCallback(async (): Promise<Session | null> => {
    const accessToken = getAccessToken()
    
    if (!accessToken) {
      setError('Please authenticate first')
      return null
    }

    setLoading(true)
    setError(null)

    try {
      // Try to create session via API
      const response = await fetch(`${CHAT_API_BASE_URL_READ}/chat/session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const raw = await response.json()
        const data = normalizeSession(raw)
        if (data.id && isValidUUID(data.id)) {
          setSession(data)
          // Intentionally do not persist session in storage (refresh should start a new session).
          console.log('[ChatBot] Created session:', data.id)
          return data
        } else {
          console.error('[ChatBot] Backend returned invalid session ID (not a UUID):', data.id)
          setError('Invalid session format received from server')
          return null
        }
      } else {
        // If session endpoint fails, don't create a local session
        // Instead, log the error and return null
        const errorText = await response.text()
        console.error('[ChatBot] Failed to create session:', response.status, errorText)
        setError(`Failed to create session: ${response.status}`)
        return null
      }
    } catch (err) {
      console.error('[ChatBot] Failed to create session:', err)
      setError('Failed to connect to chat service')
      // Don't create local session - wait for backend to be available
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const clearSession = useCallback(() => {
    setSession(null)
    setError(null)
    localStorage.removeItem(SESSION_STORAGE_KEY)
    console.log('[ChatBot] Cleared session')
  }, [])

  const startNewSession = useCallback(async (): Promise<Session | null> => {
    clearSession()
    return await createSession()
  }, [clearSession, createSession])

  // Auto-create session when authenticated and no session exists
  useEffect(() => {
    const accessToken = getAccessToken()
    if (
      accessToken && 
      !session && 
      !loading && 
      !error
    ) {
      console.log('[ChatBot] Auto-creating session...')
      createSession()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, loading, error]) // Removed createSession to prevent re-runs

  return {
    session,
    loading,
    error,
    createSession,
    clearSession,
    startNewSession,
    sessionId: session?.id || null,
  }
}


